import type { VoidFunctionComponent } from 'react';

import { SNIPPET_DETECTION } from '../../../../../shared/featureFlags/flagNames';
import { Category } from '../../../../../shared/issuesV2';
import { useShouldShowQuickImportScanMethods } from '../../../../helpers/features/quickImportScanMethods';
import FeatureFlagRequired from '../../../generic/Gates/FeatureFlagRequired';
import Loader from '../../../generic/Loader';
import usePolicies from '../../../shared/policies/hooks/usePolicies';
import Header from '../../../shared/settings/SettingsPageHeader';
import OrganizationSettingsPermissionGate from '../../OrganizationSettingsPermissionGate';
import { shouldDisplayProjectIssueSettings } from '../../shared/permissions';

import { SettingsOrganizationProjectIssuesContainers as Containers } from './Containers';
import { ExcludedSnippetRepositoriesSection } from './ExcludedSnippetRepositoriesSection';
import Licensing from './Licensing';
import Quality from './Quality';
import QuickImportScanMethodsSection from './QuickImportScanMethodsSection';
import SBOM from './SBOM';
import Security from './Security';

/**
 * SettingsOrganizationProjectIssues is the settings page for managing
 * the default issue settings for projects.
 */
const SettingsOrganizationProjectIssues: VoidFunctionComponent = () => {
  const { policies, isLoading } = usePolicies();
  const shouldShowScanMethods = useShouldShowQuickImportScanMethods();

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Header heading="Default Issue Policies Settings" />
      {shouldShowScanMethods && <QuickImportScanMethodsSection />}
      <Licensing policies={policies[Category.Licensing]} />
      <Security policies={policies[Category.Vulnerability]} />
      <Quality policies={policies[Category.Quality]} />
      <SBOM policies={policies.SBOM} />
      <Containers />
      <FeatureFlagRequired flagName={SNIPPET_DETECTION}>
        <ExcludedSnippetRepositoriesSection />
      </FeatureFlagRequired>
    </>
  );
};

/**
 * SettingsOrganizationProjectIssuesContainer displays the project issue settings page if the user has permission to view it.
 */
const SettingsOrganizationProjectIssuesContainer: VoidFunctionComponent = () => (
  <OrganizationSettingsPermissionGate checkPermission={shouldDisplayProjectIssueSettings}>
    <SettingsOrganizationProjectIssues />
  </OrganizationSettingsPermissionGate>
);

export default SettingsOrganizationProjectIssuesContainer;
